import { useEffect, useMemo, useState } from 'react';
import { createAdminManualReport } from '../api/reports.api';

export default function AdminManualReportModal({ userId, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0], // تاریخ پیش‌فرض امروز
    type: 'weekly',
    avgCalories: '',
    protein: '',
    carbs: '',
    fat: '',
    avgSteps: '',
    strengthDays: '',
    cardioDays: '',
    note: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const previews = useMemo(
    () => files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [files],
  );

  useEffect(
    () => () => previews.forEach(({ url }) => URL.revokeObjectURL(url)),
    [previews],
  );

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setError('');

    if (selectedFiles.length > 10) {
      setFiles([]);
      e.target.value = '';
      setError('حداکثر ۱۰ تصویر برای هر گزارش قابل انتخاب است.');
      return;
    }

    const invalidFile = selectedFiles.find(
      (file) => !file.type.startsWith('image/'),
    );
    if (invalidFile) {
      setFiles([]);
      e.target.value = '';
      setError(`فایل «${invalidFile.name}» تصویر معتبر نیست.`);
      return;
    }

    const oversizedFile = selectedFiles.find(
      (file) => file.size > 10 * 1024 * 1024,
    );
    if (oversizedFile) {
      setFiles([]);
      e.target.value = '';
      setError(`حجم فایل «${oversizedFile.name}» بیشتر از ۱۰ مگابایت است.`);
      return;
    }

    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);

    const formData = new FormData();
    formData.append('userId', userId);

    // اضافه کردن فیلدهای متنی
    Object.keys(form).forEach((key) => {
      formData.append(key, form[key]);
    });

    // فقط اگر عکسی انتخاب شده بود، آن را اضافه کن
    if (files && files.length > 0) {
      files.forEach((file) => formData.append('photos', file));
    }

    try {
      await createAdminManualReport(formData);
      alert('گزارش با موفقیت ثبت شد');
      onSuccess();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'خطا در ثبت گزارش؛ دوباره تلاش کنید.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
        <h2 className="text-xl font-bold mb-6 border-b pb-3">
          ثبت گزارش دستی (تلگرامی)
        </h2>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 text-sm font-vazir"
          dir="rtl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* انتخاب تاریخ - بسیار مهم برای گزارش‌های قدیمی */}
            <div>
              <label className="block mb-1 text-gray-600">
                تاریخ گزارش (میلادی)
              </label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className="w-full border p-2 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block mb-1 text-gray-600">نوع گزارش</label>
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className="w-full border p-2 rounded-xl outline-none">
                <option value="weekly">هفتگی</option>
                <option value="monthly">ماهانه</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input
              label="میانگین کالری"
              name="avgCalories"
              value={form.avgCalories}
              onChange={handleChange}
              type="number"
            />
            <Input
              label="پروتئین (%)"
              name="protein"
              value={form.protein}
              onChange={handleChange}
              type="number"
            />
            <Input
              label="کربوهیدرات (%)"
              name="carbs"
              value={form.carbs}
              onChange={handleChange}
              type="number"
            />
            <Input
              label="چربی (%)"
              name="fat"
              value={form.fat}
              onChange={handleChange}
              type="number"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label="میانگین قدم"
              name="avgSteps"
              value={form.avgSteps}
              onChange={handleChange}
              type="number"
            />
            <Input
              label="روزهای قدرتی"
              name="strengthDays"
              value={form.strengthDays}
              onChange={handleChange}
              type="number"
            />
            <Input
              label="روزهای هوازی"
              name="cardioDays"
              value={form.cardioDays}
              onChange={handleChange}
              type="number"
            />
          </div>

          <div>
            <label className="block mb-1 text-gray-600">
              یادداشت کاربر (از تلگرام کپی کنید)
            </label>
            <textarea
              name="note"
              value={form.note}
              onChange={handleChange}
              className="w-full border p-2 rounded-xl h-24 outline-none"
              placeholder="پیام کاربر را اینجا قرار دهید..."
            />
          </div>

          <div>
            <label className="block mb-1 text-gray-600">
              آپلود تصاویر (دریافت شده از تلگرام)
            </label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="w-full border p-2 rounded-xl text-xs"
              accept="image/*"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              حداکثر ۱۰ تصویر، هر فایل تا ۱۰ مگابایت
            </p>

            {previews.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
                {previews.map(({ file, url }, index) => (
                  <div key={`${file.name}-${file.lastModified}`} className="relative">
                    <img
                      src={url}
                      alt={`تصویر انتخاب‌شده ${index + 1}`}
                      className="h-20 w-full rounded-lg border object-cover"
                    />
                    <button
                      type="button"
                      aria-label={`حذف ${file.name}`}
                      onClick={() =>
                        setFiles((current) =>
                          current.filter((_, fileIndex) => fileIndex !== index),
                        )
                      }
                      className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs text-white shadow">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border rounded-xl hover:bg-gray-50 transition">
              لغو
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:bg-gray-400">
              {loading ? 'در حال ثبت...' : 'ثبت گزارش'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      <label className="block text-[11px] text-gray-500 mb-1">{label}</label>
      <input
        className="w-full border p-2 rounded-lg outline-none focus:border-blue-500 transition"
        {...props}
      />
    </div>
  );
}
